import { toWeatherCell, type WeatherPlace } from "@services/zenith/weather";
import { getLatitudeLongitude } from "@utils/getLatitudeLongitude";
import { orderOfftakes } from "@utils/orderOfftakes";

type Address = {
  location?: string | null;
  town?: string | null;
  region?: string | null;
  country?: string | null;
} | null;

/** A place the subscriber can see the weather for: one of their offtakes, or their address. */
export type WeatherLocation = {
  /** The offtake's id, or the subscriber's for their address. */
  id: string;
  /** The offtake's name, or the address's town and region; empty when the address names no town. */
  name: string;
  place: WeatherPlace;
};

// 0,0 is in the Gulf of Guinea: a placeholder, not a farm.
const coordinates = (address?: Address) => {
  const { isValid, lat, lng } = getLatitudeLongitude(address?.location ?? "");
  return isValid && (lat || lng) ? { lat: lat!, lng: lng! } : undefined;
};

/**
 * The subscriber's weather locations, their subscriber location first: their offtakes in the
 * selected scheme that have coordinates, in order, then their address, by its coordinates or
 * else its town. Without any of those, the tenant's default location.
 */
export const subscriberLocations = (
  subscriber: { id: string; address?: Address },
  offtakes: {
    id: string;
    name: string;
    isActive?: boolean;
    address?: Address;
  }[],
  fallback: WeatherLocation,
): WeatherLocation[] => {
  const { address } = subscriber;
  const town = address?.town || undefined;
  const addressPlace: WeatherPlace | undefined =
    coordinates(address) ??
    (town
      ? {
          town,
          country: address?.country || undefined,
        }
      : undefined);
  // The API shares one report across a cell: list each cell once.
  const cells = new Set<string>();

  const locations = [
    // The order Zenith lists offtakes in; the API's is the database's, which shifts.
    ...orderOfftakes(offtakes).flatMap(o => {
      const place = coordinates(o.address);
      return place ? [{ id: o.id, name: o.name, place }] : [];
    }),
    ...(addressPlace
      ? [
          {
            id: subscriber.id,
            name: town
              ? [town, address?.region].filter(Boolean).join(", ")
              : "",
            place: addressPlace,
          },
        ]
      : []),
  ].filter(({ place }) => {
    if (!("lat" in place)) return true;
    const cell = `${toWeatherCell(place.lat)},${toWeatherCell(place.lng)}`;
    if (cells.has(cell)) return false;
    cells.add(cell);
    return true;
  });
  return locations.length ? locations : [fallback];
};
